export function normalizeTargetType(typeStr) {
  if (!typeStr) return "";
  const lower = typeStr.toLowerCase();
  if (lower === "listing" || lower === "admin_listing") return "listing";
  if (lower === "order") return "order";
  if (lower === "offer") return "offer";
  if (lower === "support" || lower === "support_ticket" || lower === "admin_support") return "support_ticket";
  if (lower === "dispute") return "dispute";
  if (lower === "message" || lower === "conversation") return "conversation";
  if (lower === "user" || lower === "admin_user") return "user";
  if (lower === "admin_flagged" || lower === "flag" || lower === "behavioral" || lower === "moderation") return "flag";
  if (lower === "appeal") return "appeal";
  if (lower === "verification") return "verification";
  if (lower === "ip_block" || lower === "failed_login" || lower === "blocked_ip" || lower === "security" || lower === "ip") return "ip_block";
  return lower;
}

export function parseTargetFromUrlPath(urlStr) {
  if (!urlStr || typeof urlStr !== "string") return null;

  let match = urlStr.match(/\/order-details\/(?:sender|traveler)\/([a-f0-9]{24})/i);
  if (match) return { targetType: "order", targetId: match[1] };

  match = urlStr.match(/\/admin\/orders\/(?:details\/)?([a-f0-9]{24})/i);
  if (match) return { targetType: "order", targetId: match[1] };

  match = urlStr.match(/\/offer-details\/(?:sender|traveler)\/([a-f0-9]{24})/i);
  if (match) return { targetType: "offer", targetId: match[1] };

  match = urlStr.match(/\/support\/request-tickets\/([a-f0-9]{24})/i);
  if (match) {
    const isDispute = urlStr.includes("source=Dispute");
    return { targetType: isDispute ? "dispute" : "support_ticket", targetId: match[1] };
  }

  match = urlStr.match(/\/admin\/support-tickets\/([a-f0-9]{24})/i);
  if (match) return { targetType: "support_ticket", targetId: match[1] };

  match = urlStr.match(/\/admin\/listing-details\/(?:sender|traveler)\/([a-f0-9]{24})/i);
  if (match) return { targetType: "listing", targetId: match[1] };

  match = urlStr.match(/\/admin\/users\/userDetails\/([a-f0-9]{24})/i);
  if (match) return { targetType: "user", targetId: match[1] };

  match = urlStr.match(/\/user-listings\/(?:sender|traveler)\/(?:requests\/)?([a-f0-9]{24})/i);
  if (match) return { targetType: "listing", targetId: match[1] };

  match = urlStr.match(/\/admin\/moderation\/(?:behavioral|appeals|verification|details)?(?:\/)?([a-f0-9]{24})/i);
  if (match) return { targetType: "flag", targetId: match[1] };

  match = urlStr.match(/\/admin\/failedLogin(?:\/details|\/history)?\/([^/?#]+)/i);
  if (match) return { targetType: "ip_block", targetId: decodeURIComponent(match[1]) };

  match = urlStr.match(/\/admin\/failedLogin(?:\?.*ip=([^&]+))?/i);
  if (match && match[1]) return { targetType: "ip_block", targetId: decodeURIComponent(match[1]) };

  match = urlStr.match(/\/(?:messages|conversations)(?:\/[^/]+)?\?conversationId=([a-f0-9]{24})/i);
  if (match) return { targetType: "conversation", targetId: match[1] };

  match = urlStr.match(/\/conversations\/[^/]+\/([a-f0-9]{24})/i);
  if (match) return { targetType: "conversation", targetId: match[1] };

  return null;
}

export function parseNotificationTarget(notification) {
  if (!notification) return null;

  const results = [];

  if (notification.targetType && notification.targetId) {
    results.push({
      targetType: normalizeTargetType(notification.targetType),
      targetId: notification.targetId.toString(),
    });
  }

  // Build text combination including subject, subtitle, title, message, targetLink
  const combinedText = `${notification.title || ""} ${notification.subject || ""} ${notification.message || ""} ${notification.subtitle || ""} ${notification.targetLink || ""} ${notification.actionUrl || ""} ${notification.link || ""}`;

  // Check for readable Order IDs like BM-000079
  const bmMatch = combinedText.match(/(BM-[A-Z0-9]+)/i);
  if (bmMatch) {
    results.push({
      targetType: "order",
      targetId: bmMatch[1].toUpperCase(),
    });
  }

  // Extract Mongo IDs if present in text
  const mongoMatch = combinedText.match(/([a-f0-9]{24})/i);
  if (mongoMatch) {
    const rawType = notification.targetType || "flag";
    results.push({
      targetType: normalizeTargetType(rawType),
      targetId: mongoMatch[1],
    });
  }

  // Extract IP address if present in text
  const ipMatch = combinedText.match(/(?:IP\s+(?:address\s+)?|ipAddress["':\s]+)([0-9a-f:.]+)/i);
  if (ipMatch && ipMatch[1] && (ipMatch[1].includes(".") || ipMatch[1].includes(":"))) {
    results.push({
      targetType: "ip_block",
      targetId: ipMatch[1].trim(),
    });
  }

  const rawUrl = notification.targetLink || notification.actionUrl || notification.link;
  if (rawUrl && typeof rawUrl === "string" && rawUrl.includes("token=")) {
    try {
      const match = rawUrl.match(/token=([^&]+)/);
      if (match && match[1]) {
        const parts = match[1].split(".");
        if (parts.length === 3) {
          const payloadJson = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
          const decoded = JSON.parse(payloadJson);
          if (decoded && decoded.targetType && decoded.targetId) {
            results.push({
              targetType: normalizeTargetType(decoded.targetType),
              targetId: decoded.targetId.toString(),
            });
          }
        }
      }
    } catch (e) {
      // Fall through to regex path parsing
    }
  }

  const urlParsed = parseTargetFromUrlPath(rawUrl);
  if (urlParsed) {
    results.push(urlParsed);
  }

  return results.length > 0 ? results : null;
}

export function buildUnreadTargetsSet(unreadNotificationsList = []) {
  const set = new Set();
  if (!Array.isArray(unreadNotificationsList)) return set;

  unreadNotificationsList.forEach((n) => {
    if (n && n.read === false) {
      const targets = parseNotificationTarget(n);
      if (Array.isArray(targets)) {
        targets.forEach((parsed) => {
          if (parsed && parsed.targetType && parsed.targetId) {
            set.add(`${parsed.targetType}:${parsed.targetId}`);
            if (parsed.targetType === "flag") {
              set.add(`admin_flagged:${parsed.targetId}`);
              set.add(`dispute:${parsed.targetId}`);
              set.add(`behavioral:${parsed.targetId}`);
            }
            if (parsed.targetType === "dispute") {
              set.add(`flag:${parsed.targetId}`);
              set.add(`support_ticket:${parsed.targetId}`);
            }
            if (parsed.targetType === "support_ticket") {
              set.add(`dispute:${parsed.targetId}`);
            }
            if (parsed.targetType === "ip_block") {
              set.add(`failed_login:${parsed.targetId}`);
              set.add(`security:${parsed.targetId}`);
              set.add(`ip:${parsed.targetId}`);
            }
          }
        });
      }
    }
  });

  return set;
}
